import { FC, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Stack,
  Grid,
  TextField,
  Box,
} from "@mui/material";
import { ExpandMore, Work } from "@mui/icons-material";
import { NioshJobInfoDto } from "src/app/services/generatedApi";

interface JobInfoSectionProps {
  jobInfo: NioshJobInfoDto;
  onUpdate: (newJobInfo: NioshJobInfoDto) => void;
}

export const JobInfoSection: FC<JobInfoSectionProps> = ({
  jobInfo,
  onUpdate,
}) => {
  const [expanded, setExpanded] = useState<boolean>(false);

  const handleInputChange =
    (field: keyof NioshJobInfoDto) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onUpdate({
        ...jobInfo,
        [field]: event.target.value,
      });
    };

  const getDisplayValue = (value?: string): string => {
    return value || "";
  };

  return (
    <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)}>
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{
          borderBottom: expanded ? "1px solid" : "none",
          borderColor: "divider",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Work color="primary" />
          <Box>
            <Typography variant="h6">
              {jobInfo?.task || "NIOSH Lifting Assessment"}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {jobInfo?.company ? `${jobInfo?.company} • ` : ""}
              {jobInfo?.department || "No department specified"}
            </Typography>
          </Box>
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Date (YYYY-MM-DD)"
              value={getDisplayValue(jobInfo?.date)}
              onChange={handleInputChange("date")}
              size="small"
              type="date"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Company / Facility"
              value={getDisplayValue(jobInfo?.company)}
              onChange={handleInputChange("company")}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Department / Work Area"
              value={getDisplayValue(jobInfo?.department)}
              onChange={handleInputChange("department")}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Task / Job Name"
              value={getDisplayValue(jobInfo?.task)}
              onChange={handleInputChange("task")}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Supervisor / Person in Charge"
              value={getDisplayValue(jobInfo?.supervisor)}
              onChange={handleInputChange("supervisor")}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Evaluator / Assessor"
              value={getDisplayValue(jobInfo?.evaluator)}
              onChange={handleInputChange("evaluator")}
              size="small"
            />
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
